/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { Tooltip as BaseTooltip } from "@base-ui-components/react/tooltip";
import React from "react";

import "./Tooltip.scss";

export interface TooltipProps {
  children: React.ReactElement;
  clickCloseDelay?: number;
  placement?: BaseTooltip.Positioner.Props["side"];
  title: string;
  trigger?: "hover" | "click";
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  clickCloseDelay = 1500,
  placement = "top",
  title,
  trigger = "hover",
}) => {
  const [open, setOpen] = React.useState(false);
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const clearCloseTimeout = React.useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => clearCloseTimeout, [clearCloseTimeout]);

  const openClickTooltip = React.useCallback(() => {
    clearCloseTimeout();
    setOpen(true);
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, clickCloseDelay);
  }, [clearCloseTimeout, clickCloseDelay]);

  const clickTrigger =
    trigger === "click"
      ? React.cloneElement(children, {
          onClick: (
            event: React.MouseEvent<HTMLElement> & {
              preventBaseUIHandler?: () => void;
            },
          ) => {
            children.props.onClick?.(event);

            if (event.defaultPrevented) {
              return;
            }

            event.preventBaseUIHandler?.();
            openClickTooltip();
          },
        })
      : children;

  const handleClickOpenChange = React.useCallback<
    NonNullable<BaseTooltip.Root.Props["onOpenChange"]>
  >(
    (nextOpen) => {
      if (!nextOpen) {
        clearCloseTimeout();
        setOpen(false);
      }
    },
    [clearCloseTimeout],
  );

  const rootProps: BaseTooltip.Root.Props =
    trigger === "click"
      ? {
          closeDelay: 0,
          delay: 0,
          hoverable: false,
          onOpenChange: handleClickOpenChange,
          open,
        }
      : {};

  return (
    <BaseTooltip.Root {...rootProps}>
      <BaseTooltip.Trigger render={clickTrigger} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner side={placement}>
          <BaseTooltip.Popup className="Tooltip__Popup">
            {title}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
};
